import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Alert } from 'antd';
import { setPassword, validateToken } from '../../services/authService';
import ImageWithBasePath from '../../components/Common/imageWithBasePath';
import { useTranslation } from 'react-i18next';

const SetPasswordPage: React.FC = () => {
  const { token  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;
  const searchParams = new URLSearchParams(location.search);
  const v = searchParams.get("v");

  useEffect(() => {
    const checkToken = async () => {
      try {
        const response = await validateToken(token);
        setTokenValid(response.valid);
      } catch (err) {
        setTokenValid(false);
      }
    };
  
    if (token) checkToken();
  }, [token]);
  const handleSubmit = async (values: { password: string; confirm: string }) => {
    setLoading(true);
    setError(null);
    try {
      await setPassword({ token, password: values.password , type:v ?? undefined });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'INVALID_OR_EXPIRED_TOKEN') {
        setError("Le lien est invalide ou expiré.");
      } else {
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-page">
      <div className="main-wrapper">
          <div className="login-wrapper w-100 overflow-hidden vh-100 d-flex align-items-between justify-content-center">
              {/* RIGHT SIDE */}
              <div className="col-lg-5">
                <div className="row justify-content-center align-items-center vh-100 overflow-auto">
                  <div className="col-md-8 mx-auto p-4">
                    <div className="text-center mb-4">
                        <ImageWithBasePath
                        src="assets/img/authentication/logo-1.webp"
                        className="w-75"
                        alt="Logo"
                        />
                    </div>

                    <div className="card">
                      <div className="card-body p-4">
                        <div className="mb-3 text-center">
                        <h2 className="mb-2">{t("setup_your_password")}</h2>
                            <p>{t("setup_your_password_subtitle")}</p>
                        </div>

                        {error && <Alert message={error} type="error" className="mb-3" />}
                        {success && <Alert message="Mot de passe défini avec succès." type="success" className="mb-3" />}

                        {tokenValid === false && (
                        <Alert
                            type="error"
                            showIcon
                            message={t("invalid_or_expired_link")}
                            description={
                            <div className="mt-2">
                                <button className="btn btn-primary w-100" onClick={() => navigate('/login')}>
                                {t("back_to_login")}
                                </button>
                            </div>
                            }
                            className="mb-3"
                        />
                        )}

                        {tokenValid && !success && (
                        <Form layout="vertical" onFinish={handleSubmit}>
                            <Form.Item
                            name="password"
                            label={t("password")}
                            rules={[
                                {
                                required: true,
                                message: t("enter_password"),
                                },
                                {
                                validator(_, value) {
                                    if (!value || strongPasswordRegex.test(value)) {
                                    return Promise.resolve();
                                    }
                                    return Promise.reject(
                                    new Error(
                                        t("password_policy") ||
                                        "Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole."
                                    )
                                    );
                                },
                                },
                            ]}
                            hasFeedback
                            >
                            <Input.Password />
                            </Form.Item>

                            <Form.Item
                            label={t('confirm_password')}
                            name="confirm"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: t("confirm_your_password") },
                                ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t("passwords_do_not_match")));
                                },
                                }),
                            ]}
                            >
                            <Input.Password />
                            </Form.Item>

                            <button type="submit" className="btn btn-primary w-100">
                            {t("setup_password")}
                            </button>
                        </Form>
                        )}

                      </div>
                    </div>

                    <div className="mt-5 text-center">
                    <p className="mb-0">
                        {t("copyright")} © {new Date().getFullYear()} - Schoolation
                    </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* END RIGHT SIDE */}
            
          </div>
        </div>
    </div>
  );
};

export default SetPasswordPage;
